import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Ban, CalendarPlus } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { formatMoney } from "../../lib/pricing";
import { isoToEtLongDate, isoToEtTime, prettyTime } from "../../lib/time";
import StatusBadge from "../../components/admin/StatusBadge";

function Stat({ label, value, note, testid }) {
  return (
    <div
      className="hbe-card p-5"
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-widest text-hbe-accent">{label}</div>
      <div className="mt-2 font-serif text-3xl text-hbe-primary">{value}</div>
      {note && <div className="mt-1 text-xs text-hbe-ink/55">{note}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi
      .get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setError(String(e)));
  }, []);

  if (error) {
    return (
      <div className="text-red-700" data-testid="dashboard-error">
        Failed to load dashboard.
      </div>
    );
  }
  if (!data) {
    return <div className="text-hbe-ink/50" data-testid="dashboard-loading">Loading…</div>;
  }

  const recent = data.recent_bookings || [];

  return (
    <div data-testid="admin-dashboard">
      <div>
        <div className="hbe-eyebrow">Overview</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Dashboard</h1>
      </div>

      <div className="mt-8 grid gap-4 grid-cols-2 md:grid-cols-4">
        <Stat
          label="Today"
          value={data.today_bookings_count}
          note="Bookings starting today"
          testid="stat-today"
        />
        <Stat
          label="Upcoming"
          value={data.upcoming_bookings_count}
          note="Next 7+ days"
          testid="stat-upcoming"
        />
        <Stat
          label="Deposits (30d)"
          value={formatMoney(data.deposits_last_30_days || 0)}
          note={`All-time: ${formatMoney(data.deposits_all_time || 0)}`}
          testid="stat-deposits"
        />
        <Stat
          label="Balances (30d)"
          value={formatMoney(data.balances_last_30_days || 0)}
          note={`Collected on charter day · All-time: ${formatMoney(data.balances_all_time || 0)}`}
          testid="stat-balances"
        />
      </div>

      <div className="mt-4 hbe-card p-5" data-testid="stat-revenue-total">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-hbe-accent">
              {data.revenue_label || "Total collected (30d)"}
            </div>
            <div className="mt-1 text-xs text-hbe-ink/55">
              Deposits + balances actually collected. Charter-total commitments
              not counted until they hit the account.
            </div>
          </div>
          <div className="font-serif text-2xl text-hbe-primary">
            {formatMoney(data.revenue_last_30_days || 0)}
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/admin/yachts/new" className="hbe-btn-primary" data-testid="quick-add-yacht">
          <Plus size={16} className="mr-2" /> Add yacht
        </Link>
        <Link to="/admin/calendar" className="hbe-btn-ghost" data-testid="quick-block">
          <Ban size={16} className="mr-2" /> Block a date
        </Link>
        <Link to="/admin/calendar" className="hbe-btn-ghost" data-testid="quick-manual-booking">
          <CalendarPlus size={16} className="mr-2" /> Create manual booking
        </Link>
      </div>

      <div className="mt-12">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif text-xl">Recent bookings</h2>
          <Link
            to="/admin/bookings"
            className="text-sm text-hbe-primary hover:underline"
            data-testid="dashboard-view-all-bookings"
          >
            View all →
          </Link>
        </div>

        {recent.length === 0 ? (
          <div
            className="mt-4 hbe-card p-6 text-hbe-ink/60"
            data-testid="dashboard-recent-empty"
          >
            No bookings yet. Once customers reserve, they'll appear here.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto hbe-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-hbe-ink/55 border-b border-hbe-line">
                  <th className="px-4 py-3">Confirmation</th>
                  <th className="px-4 py-3">Date · ET</th>
                  <th className="px-4 py-3">Guest</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((b) => (
                  <tr
                    key={b.id}
                    className="border-b border-hbe-line/50 last:border-0"
                    data-testid={`recent-row-${b.confirmation_number}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-hbe-primary">
                      {b.confirmation_number}
                    </td>
                    <td className="px-4 py-3">
                      {isoToEtLongDate(b.start_datetime)} · {prettyTime(isoToEtTime(b.start_datetime))}
                    </td>
                    <td className="px-4 py-3">{b.customer_name}</td>
                    <td className="px-4 py-3">{formatMoney(b.price_total)}</td>
                    <td className="px-4 py-3"><StatusBadge status={b.booking_status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/bookings/${b.id}`}
                        className="text-sm text-hbe-primary hover:underline"
                      >
                        Detail
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
